import { useEffect, useState } from "react";
import { observer } from 'mobx-react';
import { toast } from "react-toastify";
import { Link } from 'react-router-dom';

import * as commonS from "@styles/common";

import {
  getSpecialNeeds,
  getHelpWithStudy,
} from "@helpers/getGermanNaming";

import { Button, OutlinedInput } from "@mui/material";
import Select from "@mui/material/Select";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";

import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-google-places-autocomplete";
import * as S from "./styles";
import studentsApi from "@api/students.api";

import { hasToken } from "@helpers/token";

import COLORS from "@styles/theme/colors";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const StudentsView = ({ project }) => {
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [address, setAddress] = useState(null);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [student, setStudent] = useState({});

  useEffect(() => {
    if (address) {
      geocodeByAddress(address?.label)
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) =>
          setStudent({
            ...student,
            address: address?.label,
            location: {
              type: "Point",
              coordinates: [lng, lat],
            },
            placeId: address?.value?.place_id,
          })
        );
    }
  }, [address?.label]);

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    const { name, value } = target;
    if (name === "specialNeeds" || name === "helpWithStudy") {
      setStudent({
        ...student,
        [name]: typeof value === "string" ? value.split(",") : value,
      });
    } else {
      setStudent({
        ...student,
        [name]: value,
      });
    }
  };

  useEffect(() => {
    if (
      student?.firstName &&
      student?.lastName &&
      student?.address &&
      student?.knowledgeLevel &&
      student?.gender
    ) {
      setButtonDisabled(false);
    }
  }, [student]);

  const handleAddNew = async () => {
    await studentsApi
      .addByQr({
        ...student,
        fullName: `${student?.lastName} ${student?.firstName}`,
        project: project
      })
      .then((e: any) =>
        toast.success('SuS erfolgreich hinzugefügt', { autoClose: 3000 })
      );
    await setStudent({});
    await setAddress(null);
    setButtonDisabled(true);
    setFormSubmitted(true);
  };

  return (
    <commonS.MainBody>
      <S.Wrap>
        {isFormSubmitted ?
          <>
            <h1>SuS erfolgreich hinzugefügt</h1>
            <S.ButtonRow>
              <Button variant='contained' color='primary' size='large' sx={{ minWidth: 205 }} onClick={() => setFormSubmitted(false)}>Weitere SuS hinzufügen</Button>
              {hasToken() && <Link to={`/projekte/${project}/sus`}>Back to SuS</Link>}
            </S.ButtonRow>
          </> :
          <>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="firstName" shrink={false}>
                  Vorname<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <TextField
                  value={
                    student.firstName
                  }
                  onChange={handleChange}
                  variant="outlined"
                  name="firstName"
                  id="firstName"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="lastName" shrink={false}>
                  Name<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <TextField
                  value={
                    student.lastName
                  }
                  onChange={handleChange}
                  variant="outlined"
                  name="lastName"
                  id="lastName"
                />
              </S.FormItem>
            </S.FormRow>
            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="address" shrink={false}>
                  Adresse<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <GooglePlacesAutocomplete
                  apiOptions={{ language: "de", region: "ch" }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ["ch"],
                    },
                  }}
                  selectProps={{
                    value: address,
                    onChange: setAddress,
                    styles: {
                      input: (provided: any) => ({
                        ...provided,
                        color: "#778097",
                      }),
                      control: (provided: any, state: any) => ({
                        ...provided,
                        borderColor: state.isFocused
                          ? "#4F77DE"
                          : "rgba(102, 102, 102, 0.35)",
                        outline: "none",
                        boxShadow: "none",
                        borderRadius: 8,
                        padding: "2.5px 5px",
                      }),
                      option: (provided: any) => ({
                        ...provided,
                        color: "#778097",
                      }),
                      singleValue: (provided: any) => ({
                        ...provided,
                        color: "#778097",
                      }),
                    },
                  }}
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="susId" shrink={false}>
                  SuS ID
                </InputLabel>
                <TextField
                  id="susId"
                  onChange={handleChange}
                  value={student.susId}
                  name="susId"
                  variant="outlined"
                />
              </S.FormItem>
            </S.FormRow>

            <S.FormItem>
              <InputLabel htmlFor="genderSelect" shrink={false}>
                Geschlecht<span style={{ color: COLORS.red }}>*</span>
              </InputLabel>
              <Select
                labelId="genderSelect"
                id="gender"
                label="genderSelect"
                name="gender"
                value={student.gender}
                onChange={handleChange}
              >
                <MenuItem value="male">Männlich</MenuItem>
                <MenuItem value="female">Weiblich</MenuItem>
              </Select>
            </S.FormItem>
            <S.FormItem>
              <InputLabel htmlFor="teacher" shrink={false}>
                Letzte Lehrperson
              </InputLabel>
              <TextField
                value={student.teacher}
                onChange={handleChange}
                variant="outlined"
                name="teacher"
                id="teacher"
              />
            </S.FormItem>
            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="specialNote" shrink={false}>
                  Stärken / Ressourcen
                </InputLabel>
                <TextField
                  id="specialNote"
                  onChange={handleChange}
                  value={
                    student.specialNote
                  }
                  name="specialNote"
                  variant="outlined"
                />
              </S.FormItem>
              <S.FormItem>
                <InputLabel htmlFor="note" shrink={false}>
                  Bemerkungen
                </InputLabel>
                <TextField
                  id="note"
                  onChange={handleChange}
                  value={student.note}
                  name="note"
                  variant="outlined"
                />
              </S.FormItem>
            </S.FormRow>
            <S.FormItem>
              <InputLabel htmlFor="germanLanguageLevel" shrink={false}>
                Deutschkenntnisse der Eltern
              </InputLabel>
              <Select
                labelId="germanLanguageLevel"
                id="germanLanguageLevel"
                label="germanLanguageLevel"
                name="germanLanguageLevel"
                value={
                  student.germanLanguageLevel
                }
                onChange={handleChange}
              >
                <MenuItem value="little">Genügend bis gut</MenuItem>
                <MenuItem value="high">Übersetzung notwendig</MenuItem>
              </Select>
            </S.FormItem>

            <S.FormItem>
              <InputLabel htmlFor="bestFriendName" shrink={false}>
                Freunde
              </InputLabel>
              <TextField
                id="bestFriendName"
                onChange={handleChange}
                value={
                  student.bestFriendName
                }
                name="bestFriendName"
                variant="outlined"
              />
            </S.FormItem>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="knowledgeLevel" shrink={false}>
                  Anforderungen<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <Select
                  labelId="knowledgeLevel"
                  id="knowledgeLevel"
                  label="knowledgeLevel"
                  name="knowledgeLevel"
                  value={
                    student.knowledgeLevel
                  }
                  onChange={handleChange}
                >
                  <MenuItem value="basic">Teilweise erfüllt</MenuItem>
                  <MenuItem value="intermediate">Erfüllt</MenuItem>
                  <MenuItem value="advanced">Teilweise übertroffen</MenuItem>
                </Select>
              </S.FormItem>
              <S.FormItem>
                <InputLabel htmlFor="specialNeeds" shrink={false}>
                  Fördermassnahmen
                </InputLabel>
                <Select
                  labelId="specialNeeds"
                  id="specialNeeds"
                  multiple
                  name="specialNeeds"
                  value={
                    student.specialNeeds ||
                    []
                  }
                  onChange={handleChange}
                  input={<OutlinedInput label="Fördermassnahmen" />}
                  renderValue={(selected) => selected.map(item => getSpecialNeeds(item)).join(", ")}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="germanAsSecondLanguage">
                    <Checkbox
                      checked={
                        student.specialNeeds?.indexOf(
                          "germanAsSecondLanguage"
                        ) > -1
                      }
                    />
                    <ListItemText primary="DaZ" />
                  </MenuItem>
                  <MenuItem value="logopedic">
                    <Checkbox
                      checked={
                        student.specialNeeds?.indexOf("logopedic") > -1
                      }
                    />
                    <ListItemText primary="Logopädie" />
                  </MenuItem>
                  <MenuItem value="psychomotor">
                    <Checkbox
                      checked={
                        student.specialNeeds?.indexOf("psychomotor") > -1
                      }
                    />
                    <ListItemText primary="Psychomotorik" />
                  </MenuItem>
                </Select>
              </S.FormItem>
            </S.FormRow>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="knowledgeLevelNote" shrink={false}>
                  Anforderungen wurde abgeklärt (SPL, Arzt)
                </InputLabel>
                <TextField
                  id="knowledgeLevelNote"
                  onChange={handleChange}
                  value={
                    student.knowledgeLevelNote
                  }
                  name="knowledgeLevelNote"
                  variant="outlined"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="knowledgeLevelSpecialNote" shrink={false}>
                  Anforderungen Grund
                </InputLabel>
                <TextField
                  id="knowledgeLevelSpecialNote"
                  onChange={handleChange}
                  value={
                    student.knowledgeLevelSpecialNote
                  }
                  name="knowledgeLevelSpecialNote"
                  variant="outlined"
                />
              </S.FormItem>
            </S.FormRow>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="inS" shrink={false}>
                  InS
                </InputLabel>
                <Select
                  labelId="inS"
                  id="inS"
                  label="inS"
                  name="inS"
                  value={student.inS}
                  onChange={handleChange}
                >
                  <MenuItem value=" ">Empty</MenuItem>
                  <MenuItem value="ja">Ja</MenuItem>
                  <MenuItem value="nein">Nein</MenuItem>
                </Select>
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="specialNeedsNote" shrink={false}>
                  Fördermassnahmen Weitere
                </InputLabel>
                <TextField
                  value={
                    student.specialNeedsNote
                  }
                  onChange={handleChange}
                  variant="outlined"
                  name="specialNeedsNote"
                  id="specialNeedsNote"
                />
              </S.FormItem>
            </S.FormRow>

            <S.FormItem>
              <InputLabel htmlFor="helpWithStudy" shrink={false}>
                Lernbegleitung SHP
              </InputLabel>
              <Select
                labelId="helpWithStudy"
                id="helpWithStudy"
                name="helpWithStudy"
                multiple
                value={
                  student.helpWithStudy ||
                  []
                }
                onChange={handleChange}
                input={<OutlinedInput label="Lernbegleitung SHP" />}
                renderValue={(selected) => selected.map(item => getHelpWithStudy(item)).join(", ")}
                MenuProps={MenuProps}
              >
                <MenuItem value="german">
                  <Checkbox
                    checked={
                      student.helpWithStudy?.indexOf("german") > -1
                    }
                  />
                  <ListItemText primary="Deutsch" />
                </MenuItem>
                <MenuItem value="maths">
                  <Checkbox
                    checked={
                      student.helpWithStudy?.indexOf("maths") > -1
                    }
                  />
                  <ListItemText primary="Mathe" />
                </MenuItem>
                <MenuItem value="lzaGerman">
                  <Checkbox
                    checked={
                      student.helpWithStudy?.indexOf("lzaGerman") > -1
                    }
                  />
                  <ListItemText primary="LZA Deutsch" />
                </MenuItem>
                <MenuItem value="lzaMaths">
                  <Checkbox
                    checked={
                      student.helpWithStudy?.indexOf("lzaMaths") > -1
                    }
                  />
                  <ListItemText primary="LZA Mathe" />
                </MenuItem>
              </Select>
            </S.FormItem>

            <S.ButtonRow>
              <Button variant='contained' color='primary' disabled={isButtonDisabled} size='large' sx={{ minWidth: 205 }} onClick={handleAddNew}>SuS hinzufügen</Button>
            </S.ButtonRow>
          </>
        }
      </S.Wrap>
    </commonS.MainBody>
  );
};

export default observer(StudentsView);
